<template>
  <v-container class="pa-0 mx-5">
    <v-dialog persistent v-model="saving" max-width="200">
      <v-card :loading="true">
        <p class="mt-5 title text-center">
          {{ `Saving ${entityName}...` }}
        </p>
      </v-card>
    </v-dialog>
    
    <v-row
    no-gutters
    v-if="
        Array.isArray(attributeListFiltered) &&
        attributeListFiltered.length &&
        !loading
        "
    >
      <v-col xs="12" lg="8">
        <v-row class="mt-0">
          <v-divider class="mb-6" />
        </v-row>
        
        <v-alert
        dense
        type="info"
        outlined
        class="mt-3"
        v-if="infoAlertHTML && infoAlertHTML.length"
        >
        <span v-html="infoAlertHTML" />
      </v-alert>

        <table>
          <tbody>
            <tr>
              <th>Created Time (UTC)</th>
              <td>{{ entity.created }}</td>
            </tr>
            <tr>
              <th>EMP ID</th>
              <td>{{ entity.epicEmpId }}</td>
            </tr>
            <tr v-if="entityForEdit.provider === 1">
              <th>SER ID</th>
              <td>{{ entity.epicSerId }}</td>
            </tr>
          </tbody>
        </table>

        <form-entity
        :attributeList="attributeListForEntity"
        :data-cy="`edit-${entityName}`"
        :entityName="entityName"
        :mode="mode"
        :primaryKeyName="primaryKeyName"
        @cancel="$router.push({ name: redirectName })"
        @submitCreate="submitCreate"
        @submitUpdate="submitUpdate"
        v-model="entityForEdit"
        ></form-entity>
        
      </v-col>
    </v-row>
    <generic-loader v-else></generic-loader>
  </v-container>
</template>

<script>
import "./web-component/learning-path/learning-path.js"
import { cloneDeep, get, pick } from "lodash";
import { mapActions, mapGetters } from "vuex";

import editFormMixin from "./editFormMixin";
import FormEntity from "./components/Form";
import GenericLoader from "@/components/GenericLoader";
import { formatTimestamp } from "@/lib/componentUtil";

export default {
  components: {
    FormEntity,
    GenericLoader,
  },
  computed: {
    attributeListForEntity() {
      const { attributeListFiltered, entityForEdit } = this;
      if (entityForEdit && entityForEdit.provider !== 1) {
        return attributeListFiltered.filter(a => a.name !== "ser_34825");
      }
      return attributeListFiltered;
    },
    entityLabel() {
      const { entityName, getEntityTypeByName } = this;

      return (getEntityTypeByName({ name: entityName }) || { label: "Entity" })
        .label;
    },
    redirectName() {
      const { entityName, redirectOverride } = this;

      return (
        redirectOverride || this.$route.query.redirect || `${entityName}List`
      );
    },
    ...mapGetters({
      getEntityTypeByName: "entity/find",
      getUserById: "user/getById",
    }),
  },
  async created() {
    const { entityName, id } = this;

    this.loading = true;
    try {
      await Promise.all([
        this.getAttributeList({ reload: true }),
        this.getEntityTypeList({ reload: false }),
        this.getUserList({ reload: false }),
        this.entityGetList({ reload: false }),
      ]);
    } catch (e) {
      console.error(e);
      this.$notifyError({ message: "Unable to org data." });
      return;
    }

    try {
      await this.loadEntity(id);
    } catch (e) {
      console.error(e);
      this.$notifyError({ message: `Unable to load ${entityName} data.` });
      return;
    }

    this.loading = false;

    if (!id) {
      return;
    }
  },
  data() {
    return {
      loading: true,
      entityForEdit: {},
      saving: false,
    };
  },
  methods: {
    createdInfo(entity) {
      if (typeof entity === "undefined" || typeof entity.created === "undefined") return "";

      const createdOn = `Created on ${this.formatTimestamp(entity.created)}`;

      if (typeof entity.createdBy === "undefined") return createdOn;

      const createdByUser = this.getUserById(entity.createdBy)
      if (typeof createdByUser === "undefined") return createdOn;

      const createdBy =  `${createdOn} by ${createdByUser.email}`

      return createdBy;

    },
    async entityCreate(attributeMap) {
      return this.$store.dispatch(`${this.entityName}/create`, attributeMap);
    },
    async entityUpdate([id, attributeMap]) {
      return this.$store.dispatch(`${this.entityName}/update`, [
        id,
        attributeMap,
      ]);
    },
    getEntityById(id) {
      const user = this.$store.getters["user/getByEntraId"](id);
      return user
    },
    async loadEntity(id) {
      const { mode, entityLabel } = this;
      // Make local copy for editing

      let entity;
      if (mode !== "create") {
        entity = cloneDeep(this.getEntityById(id));

        if (!entity) {
          throw new Error(`Could not load ${entityLabel}`);
        }
      } else {
        entity = {};
      }

      this.entity = entity;

      const editableAttributeNameList = this.attributeListFiltered.map(
        ({ name }) => name
      );
      // "locked" is re-added because it is needed for locations and departments to lock
      // This can be removed after completing https://jira.ochin.org/browse/TOOLS-6372
      editableAttributeNameList.push("locked");
      editableAttributeNameList.push("ser_34825");

      const entityForEdit = pick(entity, editableAttributeNameList);
      entityForEdit[this.primaryKeyName] = id;

      // Ref: https://vuejs.org/v2/guide/reactivity.html#For-Objects
      this.entityForEdit = Object.assign({}, this.entityForEdit, entityForEdit);
    },
    async submitCreate() {
      this.saving = true;
      let { entityForEdit, entityName } = this;

      try {
        await this.entityCreate(entityForEdit);
      } catch (e) {
        console.error(e);

        const statusCode = get(e, "response.status");

        if (statusCode === 409) {
          this.$notifyError({
            message: `A ${entityName} with this unique key already exists`,
            title: "Create Error",
          });
          this.saving = false;
          return;
        }

        this.$notifyError({
          message: "Unable to create new entity",
          title: "Create Error",
        });
        this.saving = false;
        return;
      }

      this.$router.push({ name: this.redirectName });

      this.$notifySuccess({
        message: `New ${entityName} created successfully`,
      });
      this.saving = false;
    },
    async submitUpdate() {
      this.saving = true;
      const { id, entityForEdit } = this;
      const clonedEntity = cloneDeep(entityForEdit);
      delete clonedEntity[this.primaryKeyName];

      try {
        await this.entityUpdate([id, clonedEntity]);
      } catch (e) {
        console.error(e);
        this.$notifyError({
          message: `Unable to update`,
        });
        this.saving = false;
        return;
      }

      this.$notifySuccess({
        message: `Successfully updated`,
      });

      this.$router.push({ name: this.redirectName });
    },
    formatTimestamp,
    ...mapActions({
      getAttributeList: "attribute/list",
      getUserList: "user/list",
      getEntityTypeList: "entity/list",
    }),
  },
  mixins: [editFormMixin],
  props: {
    id: {
      type: String,
      required: true,
    },
    infoAlertHTML: {
      type: String,
      required: false,
    },
    redirectOverride: {
      type: String,
      required: false,
    },
    showTimestampHeader: {
      default: true,
      type: Boolean,
      required: false,
    },
  },
  watch: {
    id: async function (id) {
      this.loadEntity(id);
    },
  },
};
</script>

<style>
  th {
      text-align: left;
    }
</style>
